import {
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { ItemImageArea } from 'utoniq-ui'

import {
  ResaleItemBoxFragment,
  ResaleRaffleBoxFragment,
} from '@/clients/utoniq-core/schema'
import {
  ResaleItemStatus,
  ResaleRaffleStatus,
  ResaleTag,
} from '@/components/organisms/item/ResaleTag'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Organism } from '@/core/architecture'
import { setItemSku } from '@/core/sku'

type ResaleItemBoxProps = {
  resaleItem: ResaleItemBoxFragment | ResaleRaffleBoxFragment
}

export const ResaleItemBox: Organism<ResaleItemBoxProps> = ({ resaleItem }) => {
  const { t } = useTranslation('resaleManagement')
  const { generatePath, routerPages } = useRouter()

  const data = useMemo(() => {
    if (!isResaleRaffle(resaleItem)) {
      return [
        { label: t('exhibitionDate'), value: resaleItem.formattedListedAt },
        {
          label: t('exhibitionPrice'),
          value: `¥${resaleItem.formattedTotalItemPrice}`,
        },
        { label: t('exhibitionNumber'), value: resaleItem.itemCount },
      ]
    }
    if (resaleItem.status === ResaleRaffleStatus.Purchased) {
      return [
        { label: t('purchaseDate'), value: resaleItem.formattedCreatedAt },
        {
          label: t('purchasePrice'),
          value: `¥${resaleItem.formattedTotalItemPrice}`,
        },
        { label: t('purchaseNumber'), value: resaleItem.itemCount },
      ]
    }
    return [
      { label: t('applicationDate'), value: resaleItem.formattedCreatedAt },
      {
        label: t('applicationPrice'),
        value: `¥${resaleItem.formattedTotalItemPrice}`,
      },
      { label: t('applicationNumber'), value: resaleItem.itemCount },
    ]
  }, [resaleItem, t])

  const href = useMemo<string>(() => {
    if (!isResaleRaffle(resaleItem)) {
      return generatePath(routerPages.ResaleDetail, {
        resaleItemId: resaleItem.id,
      })
    }

    return generatePath(routerPages.ResaleRaffleDetail, {
      itemId: resaleItem.item.id,
      raffleId: resaleItem.id,
    })
  }, [generatePath, resaleItem])

  const skuName = useMemo(() => {
    return (
      setItemSku(resaleItem?.item, resaleItem?.skuId ?? null).sku?.unitName ??
      ''
    )
  }, [resaleItem])

  return (
    <Card elevation={0}>
      <CardActionArea href={href}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 4 } }}>
          <Stack direction="row" spacing={2}>
            <Stack flex={1} spacing={1}>
              {resaleItem.status !== ResaleRaffleStatus.Purchased && (
                <Stack direction="row" alignItems="center" spacing={2}>
                  <ResaleTag status={resaleItem.status} />
                  {resaleItem.status === ResaleItemStatus.Paid && (
                    <Typography variant="subtitle2" color="primary">
                      {t('transferCompleted')}
                    </Typography>
                  )}
                </Stack>
              )}
              <Typography sx={styleSets.lineCamp(2)} variant="h5">
                {resaleItem.item.title}
              </Typography>
              {!!skuName && (
                <Typography sx={styleSets.lineCamp(2)} variant="caption">
                  {skuName}
                </Typography>
              )}
              <Stack spacing={1}>
                {data.map(({ label, value }, index) => (
                  <Stack direction="row" spacing={3} key={index}>
                    <Typography variant="overline" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="overline">{value}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
            <ItemImageArea
              size="medium-small"
              src={resaleItem.item.thumbnail.front?.mediumUri ?? ''}
            />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

function isResaleRaffle(
  resale: ResaleItemBoxFragment | ResaleRaffleBoxFragment
): resale is ResaleRaffleBoxFragment {
  return 'createdAt' in resale
}
